/**
 * Le profil public d'un candidat : activité, études, adage, trois mots.
 *
 * Les bornes sont celles du serveur (`app/schemas/competition.py`) : les
 * vérifier ici ne remplace pas son contrôle, cela évite seulement d'envoyer
 * un formulaire dont on sait déjà qu'il sera refusé — et permet de dire,
 * dans la langue de la personne, quel champ corriger.
 */

export const ACTIVITY_MAX = 120
export const EDUCATION_MAX = 120
export const MOTTO_MAX = 160
export const WORD_MAX = 30
export const AGE_MIN = 16
export const AGE_MAX = 120

/**
 * Suggestions de niveau d'études. Le champ reste libre : aucune liste fermée
 * ne couvre à la fois les cursus allemands, français et camerounais. La
 * suggestion choisie s'enregistre telle qu'elle est lue, dans la langue du
 * formulaire.
 */
export const EDUCATION_SUGGESTIONS = [
  'highSchool',
  'vocational',
  'bachelor',
  'master',
  'doctorate',
  'other',
] as const

export type ProfileField = 'age' | 'activity' | 'education_level' | 'motto' | 'three_words'

/** Une erreur : une clé de traduction et ses paramètres. */
export interface ProfileError {
  key: string
  params?: Record<string, number>
}

export interface ProfileValues {
  age: string
  activity: string
  educationLevel: string
  motto: string
  words: [string, string, string]
}

/**
 * Les erreurs du profil, champ par champ.
 *
 * `required` : vrai au dépôt public, où tout est exigé ; faux en
 * administration, où l'équipe complète ce qu'elle peut — mais où trois mots
 * restent trois mots, ou aucun.
 */
export function validateProfile(
  values: ProfileValues,
  { required }: { required: boolean },
): Partial<Record<ProfileField, ProfileError>> {
  const errors: Partial<Record<ProfileField, ProfileError>> = {}

  const age = values.age.trim()
  if (!age) {
    if (required) errors.age = { key: 'candidateProfile.errors.required' }
  } else {
    const parsed = Number(age)
    if (!Number.isInteger(parsed) || parsed < AGE_MIN || parsed > AGE_MAX) {
      errors.age = {
        key: 'candidateProfile.errors.ageRange',
        params: { min: AGE_MIN, max: AGE_MAX },
      }
    }
  }

  const text = (
    field: 'activity' | 'education_level' | 'motto',
    value: string,
    max: number,
  ) => {
    const trimmed = value.trim()
    if (!trimmed && required) errors[field] = { key: 'candidateProfile.errors.required' }
    else if (trimmed.length > max) {
      errors[field] = { key: 'candidateProfile.errors.tooLong', params: { max } }
    }
  }
  text('activity', values.activity, ACTIVITY_MAX)
  text('education_level', values.educationLevel, EDUCATION_MAX)
  text('motto', values.motto, MOTTO_MAX)

  const words = values.words.map((word) => word.trim())
  const filled = words.filter(Boolean)
  // En administration, trois cases vides valent « pas de mots ».
  if (required || filled.length > 0) {
    if (filled.length !== 3) {
      errors.three_words = { key: 'candidateProfile.errors.threeWordsCount' }
    } else if (words.some((word) => word.length > WORD_MAX)) {
      errors.three_words = {
        key: 'candidateProfile.errors.wordTooLong',
        params: { max: WORD_MAX },
      }
    } else if (new Set(words.map((word) => word.toLocaleLowerCase())).size !== 3) {
      errors.three_words = { key: 'candidateProfile.errors.threeWordsDuplicate' }
    }
  }

  return errors
}

/** Les trois mots tels qu'on les envoie : nettoyés, ou aucun. */
export function wordsPayload(words: readonly string[]): string[] {
  const trimmed = words.map((word) => word.trim())
  return trimmed.some(Boolean) ? trimmed : []
}

/** Trois cases, quoi que contienne la fiche (une ancienne en a zéro). */
export function toWordSlots(words: readonly string[] | null | undefined): [string, string, string] {
  return [words?.[0] ?? '', words?.[1] ?? '', words?.[2] ?? '']
}

/**
 * Les champs qu'un refus du serveur (422) désigne, ramenés à ceux du profil.
 * `three_words.1` vise la liste entière : on corrige les trois cases ensemble.
 */
export function serverProfileFields(
  details: ReadonlyArray<{ field?: string | undefined }>,
): ProfileField[] {
  const known: ProfileField[] = ['age', 'activity', 'education_level', 'motto', 'three_words']
  return known.filter((field) =>
    details.some((detail) => detail.field === field || detail.field?.startsWith(`${field}.`)),
  )
}
